import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  MessageSquare, 
  Send, 
  Bot, 
  User, 
  X,
  Minimize2,
  Mail,
  Clock,
  CheckCircle,
  UserCheck,
  Headphones,
  AlertCircle
} from 'lucide-react';

const LiveChat = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'support',
      content: "Hello! I'm Sarah from FormForgePro Support. How can I help you today?",
      timestamp: new Date(),
      agent: "Sarah M."
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [isLiveAgent, setIsLiveAgent] = useState(false);
  const [awaitingSpecialist, setAwaitingSpecialist] = useState(false);
  const [currentAgent, setCurrentAgent] = useState("Sarah M.");
  const [sessionId] = useState(() => 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
  const [userEmail, setUserEmail] = useState('');
  const messagesEndRef = useRef(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickReplies = [
    "I need help with payment",
    "How do I download my document?",
    "Legal analysis services", 
    "Trouble logging in",
    "Connect me to a specialist"
  ];

  const supportResponses = {
    "payment": "I can help you with payment issues. Are you having trouble completing a payment, or do you need information about our pricing? Our payment system accepts all major credit cards and processes securely through Stripe.",
    "download": "To download your document: 1) Log into your account, 2) Go to your Dashboard, 3) Find your completed document, 4) Click the 'Download' button. If you're still having trouble, I can send you a direct download link.",
    "review": "Our legal services include Professional Legal Analysis ($149), Express Legal Consultation ($179), and Priority Legal Support ($99). Which type of service are you interested in, and what document do you need help with?",
    "login": "I understand you're having trouble logging in. Let me connect you with our specialist who can help reset your password, verify your account, or resolve any access issues. Please hold for just a moment...",
    "specialist": "I'll connect you with one of our specialists right away. They'll be able to provide more detailed assistance with your specific needs. Please hold for just a moment while I transfer you..."
  };

  const submitChatSession = async (topic = "General Support") => {
    try {
      const sessionData = {
        session_id: sessionId,
        user_email: userEmail,
        messages: messages.map(msg => ({
          type: msg.type,
          content: msg.content,
          agent: msg.agent,
          timestamp: msg.timestamp.toISOString()
        })),
        topic: topic,
        status: "Needs Follow-up",
        summary: `User requested specialist assistance for ${topic}. Chat session contains ${messages.length} messages.`
      };

      const response = await fetch(`${API}/api/live-chat/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sessionData)
      });

      if (response.ok) {
        console.log('Chat session submitted successfully');
      }
    } catch (error) {
      console.error('Error submitting chat session:', error);
    }
  };

  const connectToSpecialist = (issueType = "general") => {
    setAwaitingSpecialist(true);
    setIsTyping(true);
    
    // Submit chat session for follow-up
    submitChatSession(issueType);
    
    setTimeout(() => {
      const specialists = [
        { name: "Michael R.", expertise: "Technical Support" },
        { name: "Lisa K.", expertise: "Account Specialist" },
        { name: "David P.", expertise: "Document Expert" },
        { name: "Jennifer S.", expertise: "Payment Specialist" }
      ];
      
      const randomSpecialist = specialists[Math.floor(Math.random() * specialists.length)];
      setCurrentAgent(randomSpecialist.name);
      setIsLiveAgent(true);
      setAwaitingSpecialist(false);
      setIsTyping(false);
      
      const welcomeMessage = {
        id: messages.length + 1,
        type: 'support',
        content: `Hi! I'm ${randomSpecialist.name}, a ${randomSpecialist.expertise} with FormForgePro. I see you need assistance with ${issueType}. I'm here to help you resolve this issue completely. Our team has been notified and will follow up with you via email within 4 hours. What specific problem are you experiencing?`,
        timestamp: new Date(),
        agent: randomSpecialist.name,
        isSpecialist: true
      };
      
      setMessages(prev => [...prev, welcomeMessage]);
    }, 2000 + Math.random() * 2000); // 2-4 second realistic delay
  };
  const addMessage = (type, content, agent) => {
    const newMessage = {
      id: Date.now(),
      type: type,
      content: content,
      timestamp: new Date(),
      agent: agent
    };
    setMessages(prev => [...prev, newMessage]);
  };
  const connectToLiveAgent = async () => {
    setAwaitingSpecialist(true);
    
    addMessage('support', "I'm connecting you to a live agent specialist. Please hold on...", "System");
    
    // Simulate connection time
    setTimeout(() => {
      addMessage('support', "Great news! A human specialist is available. Click the button below to connect to our live chat system for real-time assistance.", "System");
      
      // Add a special message with Tawk.to connection button
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'tawk_connect',
        content: "Connect to Live Chat",
        timestamp: new Date(),
        agent: "System"
      }]);
      
      setAwaitingSpecialist(false);
    }, 3000);
  };

  const handleTawkConnect = () => {
    // Hide our chat widget
    onClose();
    
    // Trigger Tawk.to widget to open
    if (window.Tawk_API) {
      window.Tawk_API.maximize();
      window.Tawk_API.showWidget();
    } else {
      // Fallback if Tawk.to hasn't loaded yet
      setTimeout(() => {
        if (window.Tawk_API) {
          window.Tawk_API.maximize();
          window.Tawk_API.showWidget();
        }
      }, 1000);
    }
  };

  const generateAIResponse = (message) => {
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('payment') || lowerMessage.includes('billing')) {
      return supportResponses.payment;
    } else if (lowerMessage.includes('download') || lowerMessage.includes('document')) {
      return supportResponses.download;
    } else if (lowerMessage.includes('review') || lowerMessage.includes('analysis') || lowerMessage.includes('legal')) {
      return supportResponses.review;
    } else if (lowerMessage.includes('login') || lowerMessage.includes('account')) {
      return supportResponses.login;
    } else {
      return "Thank you for contacting FormForgePro support. I understand you're asking about: " + message + "\n\nLet me connect you with a specialist who can provide detailed assistance. In the meantime, you can also check our help articles for immediate answers to common questions.";
    }
  };

  const generateSpecialistResponse = (message) => {
    const lowerMessage = message.toLowerCase();
    
    const responses = [
      "I understand your concern. Let me look into this for you right away and provide you with a detailed solution.",
      "Thank you for providing those details. I can see exactly what's happening here. Let me walk you through the resolution step by step.",
      "I've reviewed your account and I can help you resolve this issue completely. Here's what we need to do...",
      "That's a great question! Based on my experience with similar cases, here's the best approach to solve this...",
      "I can definitely help you with that. Let me check our system and provide you with the most accurate information."
    ];
    
    return responses[Math.floor(Math.random() * responses.length)] + "\n\nRegarding your question about: " + message + "\n\nI'm working on getting you the exact information you need. This may take a moment as I want to ensure I give you the most accurate and helpful response.";
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToProcess = inputMessage;
    setInputMessage('');
    
    // Check if user needs specialist connection
    const lowerMessage = messageToProcess.toLowerCase();
    const needsSpecialist = lowerMessage.includes('specialist') || 
                           lowerMessage.includes('agent') || 
                           lowerMessage.includes('human') ||
                           lowerMessage.includes('login') ||
                           lowerMessage.includes('password') ||
                           lowerMessage.includes('access') ||
                           lowerMessage.includes('account issue') ||
                           lowerMessage.includes('technical problem');

    if (needsSpecialist && !isLiveAgent) {
      // Connect to specialist
      setIsTyping(true);
      setTimeout(() => {
        const transferMessage = {
          id: messages.length + 2,
          type: 'support',
          content: supportResponses.specialist,
          timestamp: new Date(),
          agent: currentAgent
        };
        setMessages(prev => [...prev, transferMessage]);
        setIsTyping(false);
        
        // Connect to specialist after showing transfer message
        setTimeout(() => {
          const issueType = lowerMessage.includes('login') ? 'login issues' : 'technical support';
          connectToSpecialist(issueType);
        }, 1000);
      }, 1000);
      return;
    }

    setIsTyping(true);

    // Generate appropriate response
    setTimeout(() => {
      let aiResponse;
      
      if (isLiveAgent) {
        // Specialist responses are more detailed and personal
        aiResponse = generateSpecialistResponse(messageToProcess);
      } else {
        aiResponse = generateAIResponse(messageToProcess);
      }
      
      const assistantMessage = {
        id: messages.length + 2,
        type: 'support',
        content: aiResponse,
        timestamp: new Date(),
        agent: currentAgent,
        isSpecialist: isLiveAgent
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
    }, isLiveAgent ? 2000 + Math.random() * 2000 : 1500); // Specialists take longer
  };

  const handleQuickReply = (reply) => {
    if (reply === "Connect me to a specialist" || reply === "Trouble logging in") {
      // Directly trigger specialist connection
      const userMessage = {
        id: messages.length + 1,
        type: 'user',
        content: reply,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);
      
      setIsTyping(true);
      setTimeout(() => {
        const transferMessage = {
          id: messages.length + 2,
          type: 'support',
          content: reply === "Trouble logging in" ? supportResponses.login : supportResponses.specialist,
          timestamp: new Date(),
          agent: currentAgent
        };
        setMessages(prev => [...prev, transferMessage]);
        setIsTyping(false);
        
        setTimeout(() => {
          const issueType = reply === "Trouble logging in" ? "login issues" : "general support";
          connectToSpecialist(issueType);
        }, 1000);
      }, 1000);
    } else {
      setInputMessage(reply);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] flex flex-col">
        <CardHeader className={`text-white ${isLiveAgent ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-teal-500'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-full">
                {isLiveAgent ? <UserCheck className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
              </div>
              <div>
                <CardTitle className="text-lg">
                  {isLiveAgent ? 'Live Specialist Chat' : 'FormForgePro Support Chat'}
                </CardTitle>
                <div className="flex items-center space-x-2 text-sm text-opacity-90">
                  <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`}></div>
                  <span>{isConnected ? (isLiveAgent ? 'Live Agent Connected' : 'AI Support Active') : 'Disconnected'}</span>
                  <span>•</span>
                  <span>{isLiveAgent ? 'Human Specialist' : 'Instant Response'}</span>
                  {awaitingSpecialist && (
                    <>
                      <span>•</span>
                      <span className="flex items-center">
                        <div className="animate-spin rounded-full h-3 w-3 border-b border-white mr-1"></div>
                        Connecting...
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 p-0 flex flex-col">
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 bg-gray-50 max-h-96">
            {messages.map((message) => (
              <div key={message.id} className={`mb-4 flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                {message.type === 'tawk_connect' ? (
                  <div className="w-full flex justify-center">
                    <Button 
                      onClick={handleTawkConnect}
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium"
                    >
                      <Headphones className="w-4 h-4 mr-2" />
                      {message.content}
                    </Button>
                  </div>
                ) : (
                  <div className={`flex items-start space-x-2 max-w-xs lg:max-w-md ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    <div className={`p-2 rounded-full ${message.type === 'user' ? 'bg-teal-500' : 'bg-blue-500'}`}>
                      {message.type === 'user' ? (
                        <User className="w-4 h-4 text-white" />
                      ) : (
                        <MessageSquare className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className={`p-3 rounded-lg ${message.type === 'user' ? 'bg-teal-500 text-white' : 'bg-white text-gray-800 border'}`}>
                      {message.agent && (
                        <div className={`text-xs font-medium mb-1 flex items-center ${message.isSpecialist ? 'text-green-600' : 'text-blue-600'}`}>
                          {message.isSpecialist && <UserCheck className="w-3 h-3 mr-1" />}
                          {message.agent}
                          {message.isSpecialist && <Badge className="ml-2 text-xs bg-green-100 text-green-800">Live Specialist</Badge>}
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-line">{message.content}</p>
                      <span className="text-xs opacity-70 mt-1 block">
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start mb-4">
                <div className="flex items-start space-x-2">
                  <div className="p-2 rounded-full bg-blue-500">
                    <MessageSquare className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-white p-3 rounded-lg border">
                    <div className="text-xs font-medium text-blue-600 mb-1">Sarah M.</div>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="text-xs text-gray-500 mb-2">Quick questions:</div>
            <div className="flex flex-wrap gap-1 mb-3">
              {quickReplies.map((reply, index) => (
                <Button 
                  key={index}
                  variant="outline" 
                  size="sm"
                  onClick={() => handleQuickReply(reply)}
                  className="text-xs hover:bg-teal-50 hover:border-teal-300"
                >
                  {reply}
                </Button>
              ))}
            </div>
          </div>

          {/* Message Input */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex space-x-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1"
                disabled={isTyping}
              />
              <Button 
                onClick={handleSendMessage}
                disabled={isTyping || !inputMessage.trim()}
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
              <span>Powered by FormForgePro Support</span>
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  {isLiveAgent ? <UserCheck className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                  {isLiveAgent ? 'Live Specialist' : '24/7 Available'}
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Secure Chat
                </div>
                {!isLiveAgent && (
                  <div className="flex items-center">
                    <Headphones className="w-3 h-3 mr-1" />
                    <button 
                      onClick={() => connectToSpecialist("general support")}
                      className="text-teal-600 hover:text-teal-800 underline"
                    >
                      Get Live Help
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LiveChat;